import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import {
  Plus,
  Trash2,
  FolderOpen,
  Edit,
  Loader2,
  ChevronRight,
  ChevronDown,
  LayoutDashboard,
  RefreshCw,
  Eye,
} from 'lucide-react';
import {
  barrattAPI,
  type AnalyseCategory,
  type AnalyseDashboard,
} from '../../lib/api/barratt';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { EditCategoryDialog, AddDashboardDialog, EditDashboardDialog } from './CategoryDialogs';

// Sharing level display
const SHARING_LABELS: Record<string, { label: string; color: string }> = {
  private: { label: 'Private', color: 'bg-gray-100 text-gray-800' },
  company: { label: 'Company', color: 'bg-blue-100 text-blue-800' },
  company_and_suppliers: { label: 'Shared', color: 'bg-green-100 text-green-800' },
};

export default function Category() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<AnalyseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [categoryDashboards, setCategoryDashboards] = useState<Record<number, AnalyseDashboard[]>>({});
  const [loadingDashboards, setLoadingDashboards] = useState<Set<number>>(new Set());

  // Category dialog state
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryHint, setNewCategoryHint] = useState('');
  const [isAddCategoryDialogOpen, setIsAddCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<AnalyseCategory | null>(null);
  const [isEditCategoryDialogOpen, setIsEditCategoryDialogOpen] = useState(false);

  // Dashboard dialog state
  const [isAddDashboardDialogOpen, setIsAddDashboardDialogOpen] = useState(false);
  const [isEditDashboardDialogOpen, setIsEditDashboardDialogOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [editingDashboard, setEditingDashboard] = useState<AnalyseDashboard | null>(null);
  const [newDashboard, setNewDashboard] = useState({
    name: '',
    description: '',
    is_enabled: true,
    shared_with: 'private' as 'private' | 'company' | 'company_and_suppliers',
  });

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await barrattAPI.getCategories();
      setCategories(response.data.results || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  // Fetch dashboards for a category
  const fetchDashboards = async (categoryId: number) => {
    setLoadingDashboards((prev) => new Set(prev).add(categoryId));
    try {
      const response = await barrattAPI.getDashboards({ category: categoryId });
      setCategoryDashboards((prev) => ({
        ...prev,
        [categoryId]: response.data.results || [],
      }));
    } catch (error) {
      console.error('Failed to fetch dashboards:', error);
      toast.error('Failed to load dashboards');
    } finally {
      setLoadingDashboards((prev) => {
        const newSet = new Set(prev);
        newSet.delete(categoryId);
        return newSet;
      });
    }
  };

  // Toggle category expansion
  const toggleCategory = (categoryId: number) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
      // Fetch dashboards if not already loaded
      if (!categoryDashboards[categoryId]) {
        fetchDashboards(categoryId);
      }
    }
    setExpandedCategories(newExpanded);
  };

  // Category CRUD
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const response = await barrattAPI.createCategory({
        name: newCategoryName.trim(),
        hint: newCategoryHint.trim() || undefined,
      });
      setCategories([...categories, response.data]);
      setNewCategoryName('');
      setNewCategoryHint('');
      setIsAddCategoryDialogOpen(false);
      toast.success('Category created successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create category';
      toast.error(errorMessage);
    }
  };

  const handleEditCategory = async () => {
    if (!editingCategory || !editingCategory.name.trim()) return;

    try {
      const response = await barrattAPI.updateCategory(editingCategory.id, {
        name: editingCategory.name.trim(),
        hint: editingCategory.hint?.trim(),
      });
      setCategories(categories.map((cat) =>
        cat.id === editingCategory.id ? response.data : cat
      ));
      setEditingCategory(null);
      setIsEditCategoryDialogOpen(false);
      toast.success('Category updated successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update category';
      toast.error(errorMessage);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    try {
      await barrattAPI.deleteCategory(id);
      setCategories(categories.filter((cat) => cat.id !== id));
      // Clean up expanded state and cached dashboards
      setExpandedCategories((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      setCategoryDashboards((prev) => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
      toast.success('Category deleted successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete category';
      toast.error(errorMessage);
    }
  };

  // Dashboard CRUD
  const openAddDashboardDialog = (categoryId: number) => {
    setSelectedCategoryId(categoryId);
    setNewDashboard({
      name: '',
      description: '',
      is_enabled: true,
      shared_with: 'private',
    });
    setIsAddDashboardDialogOpen(true);
  };

  const handleAddDashboard = async () => {
    if (!newDashboard.name.trim() || !selectedCategoryId) return;

    try {
      const response = await barrattAPI.createDashboard({
        name: newDashboard.name.trim(),
        description: newDashboard.description.trim() || undefined,
        is_enabled: newDashboard.is_enabled,
        shared_with: newDashboard.shared_with,
        category: selectedCategoryId,
      });

      // Update cached dashboards
      setCategoryDashboards((prev) => ({
        ...prev,
        [selectedCategoryId]: [...(prev[selectedCategoryId] || []), response.data],
      }));

      // Update category dashboard count
      setCategories(categories.map((cat) =>
        cat.id === selectedCategoryId
          ? { ...cat, dashboard_count: (cat.dashboard_count || 0) + 1 }
          : cat
      ));

      setIsAddDashboardDialogOpen(false);
      toast.success('Dashboard created successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create dashboard';
      toast.error(errorMessage);
    }
  };

  const openEditDashboardDialog = (dashboard: AnalyseDashboard) => {
    setEditingDashboard({ ...dashboard });
    setIsEditDashboardDialogOpen(true);
  };

  const handleEditDashboard = async () => {
    if (!editingDashboard || !editingDashboard.name.trim()) return;

    try {
      const response = await barrattAPI.updateDashboard(editingDashboard.id, {
        name: editingDashboard.name.trim(),
        description: editingDashboard.description?.trim(),
        is_enabled: editingDashboard.is_enabled,
        shared_with: editingDashboard.shared_with,
      });

      // Update cached dashboards
      if (editingDashboard.category) {
        setCategoryDashboards((prev) => ({
          ...prev,
          [editingDashboard.category!]: (prev[editingDashboard.category!] || []).map((d) =>
            d.id === editingDashboard.id ? response.data : d
          ),
        }));
      }

      setEditingDashboard(null);
      setIsEditDashboardDialogOpen(false);
      toast.success('Dashboard updated successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update dashboard';
      toast.error(errorMessage);
    }
  };

  const handleDeleteDashboard = async (dashboard: AnalyseDashboard) => {
    try {
      await barrattAPI.deleteDashboard(dashboard.id);

      // Update cached dashboards
      if (dashboard.category) {
        setCategoryDashboards((prev) => ({
          ...prev,
          [dashboard.category!]: (prev[dashboard.category!] || []).filter((d) => d.id !== dashboard.id),
        }));

        // Update category dashboard count
        setCategories(categories.map((cat) =>
          cat.id === dashboard.category
            ? { ...cat, dashboard_count: Math.max((cat.dashboard_count || 1) - 1, 0) }
            : cat
        ));
      }

      toast.success('Dashboard deleted successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete dashboard';
      toast.error(errorMessage);
    }
  };

  // View dashboard in Analyse page
  const viewDashboard = (dashboard: AnalyseDashboard) => {
    navigate(`/barratt/analyse?dashboard=${dashboard.id}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Category Management</h1>
          <p className="text-muted-foreground">
            Manage analyse categories and dashboards
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchCategories} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Dialog open={isAddCategoryDialogOpen} onOpenChange={setIsAddCategoryDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Category</DialogTitle>
                <DialogDescription>
                  Create a new category to organize your dashboards
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Category Name *</label>
                  <Input
                    placeholder="Enter category name"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Description</label>
                  <Textarea
                    placeholder="Enter description (optional)"
                    value={newCategoryHint}
                    onChange={(e) => setNewCategoryHint(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddCategoryDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddCategory} disabled={!newCategoryName.trim()}>
                  Add Category
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Categories List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Categories
          </CardTitle>
          <CardDescription>
            Click on a category to expand and view its dashboards
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                No categories created. Click "Add Category" to create one.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {categories.map((category) => {
                const isExpanded = expandedCategories.has(category.id);
                const dashboards = categoryDashboards[category.id] || [];
                const isLoadingDashboards = loadingDashboards.has(category.id);

                return (
                  <Collapsible
                    key={category.id}
                    open={isExpanded}
                    onOpenChange={() => toggleCategory(category.id)}
                  >
                    <div className="border rounded-lg">
                      {/* Category Row */}
                      <div className="flex items-center justify-between p-4 hover:bg-muted/50">
                        <CollapsibleTrigger asChild>
                          <div className="flex items-center gap-3 cursor-pointer flex-1">
                            {isExpanded ? (
                              <ChevronDown className="h-5 w-5 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-muted-foreground" />
                            )}
                            <FolderOpen className="h-5 w-5 text-primary" />
                            <div>
                              <p className="font-medium">{category.name}</p>
                              {category.hint && (
                                <p className="text-sm text-muted-foreground">{category.hint}</p>
                              )}
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <div className="flex items-center gap-4">
                          <Badge variant="secondary">
                            {category.dashboard_count || 0} dashboards
                          </Badge>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingCategory({ ...category });
                                setIsEditCategoryDialogOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Category</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{category.name}"? This will also
                                    delete all dashboards within this category. This action cannot be
                                    undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteCategory(category.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </div>

                      {/* Dashboards List (Collapsed Content) */}
                      <CollapsibleContent>
                        <div className="border-t bg-muted/30 p-4">
                          {isLoadingDashboards ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            </div>
                          ) : dashboards.length === 0 ? (
                            <div className="text-center py-4">
                              <p className="text-sm text-muted-foreground mb-3">
                                No dashboards in this category
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openAddDashboardDialog(category.id)}
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Dashboard
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-muted-foreground">
                                  Dashboards
                                </h4>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openAddDashboardDialog(category.id)}
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add Dashboard
                                </Button>
                              </div>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Sharing</TableHead>
                                    <TableHead className="text-right">Indicators</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {dashboards.map((dashboard) => (
                                    <TableRow key={dashboard.id}>
                                      <TableCell>
                                        <div className="flex items-center gap-2">
                                          <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
                                          <div>
                                            <p className="font-medium">{dashboard.name}</p>
                                            {dashboard.description && (
                                              <p className="text-xs text-muted-foreground">
                                                {dashboard.description}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <Badge
                                          variant={dashboard.is_enabled ? 'default' : 'secondary'}
                                        >
                                          {dashboard.is_enabled ? 'Active' : 'Disabled'}
                                        </Badge>
                                      </TableCell>
                                      <TableCell>
                                        <Badge
                                          className={SHARING_LABELS[dashboard.shared_with]?.color}
                                        >
                                          {SHARING_LABELS[dashboard.shared_with]?.label}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {dashboard.indicator_count || 0}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => viewDashboard(dashboard)}
                                            title="View Dashboard"
                                          >
                                            <Eye className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => openEditDashboardDialog(dashboard)}
                                            title="Edit Dashboard"
                                          >
                                            <Edit className="h-4 w-4" />
                                          </Button>
                                          <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="text-destructive"
                                                title="Delete Dashboard"
                                              >
                                                <Trash2 className="h-4 w-4" />
                                              </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                              <AlertDialogHeader>
                                                <AlertDialogTitle>Delete Dashboard</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                  Are you sure you want to delete "{dashboard.name}"?
                                                  This will also delete all indicators within this
                                                  dashboard.
                                                </AlertDialogDescription>
                                              </AlertDialogHeader>
                                              <AlertDialogFooter>
                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                <AlertDialogAction
                                                  onClick={() => handleDeleteDashboard(dashboard)}
                                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                >
                                                  Delete
                                                </AlertDialogAction>
                                              </AlertDialogFooter>
                                            </AlertDialogContent>
                                          </AlertDialog>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Category Dialog */}
      <EditCategoryDialog
        open={isEditCategoryDialogOpen}
        onOpenChange={setIsEditCategoryDialogOpen}
        editingCategory={editingCategory}
        onEditingCategoryChange={setEditingCategory}
        onSave={handleEditCategory}
      />

      {/* Add Dashboard Dialog */}
      <AddDashboardDialog
        open={isAddDashboardDialogOpen}
        onOpenChange={setIsAddDashboardDialogOpen}
        dashboardData={newDashboard}
        onDashboardDataChange={setNewDashboard}
        onSave={handleAddDashboard}
      />

      {/* Edit Dashboard Dialog */}
      <EditDashboardDialog
        open={isEditDashboardDialogOpen}
        onOpenChange={setIsEditDashboardDialogOpen}
        editingDashboard={editingDashboard}
        onEditingDashboardChange={setEditingDashboard}
        onSave={handleEditDashboard}
      />
    </div>
  );
}
